/**
 * What the model is told of a document the user attached that the gateway did not store (a page's text): the words
 * that introduce it. Stored files go to the gateway by id, and the assistant's own reads are the gateway's
 * (`fetch_url`, ml-services src/agent/reads.ts).
 */

import type { GatewayDocument } from './gateway';

function megabytes(bytes: number): string {
    return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

/** A document as words: what it is and where `fetch_url` reads it, or a page's text cut to `textBudget`. `origin` says where it came from. */
export function documentForModel(doc: GatewayDocument, origin: string, textBudget = 6000): string {
    switch (doc.kind) {
        case 'pdf':
            return `${origin} ${doc.filename} (PDF, ${doc.pageCount} pages, ${megabytes(doc.bytes)}) from ${doc.url}. `
                + (doc.note ?? 'Call fetch_url with that URL, and `pages` for the part you need, to read it.');
        case 'image':
            return `${origin} ${doc.filename} (${doc.mime}, ${megabytes(doc.bytes)}) from ${doc.url}. Call fetch_url with that URL to see it.`;
        case 'html': {
            // Links first: they are what a model follows (an instruction PDF on
            // a product page), and the page text is what gets cut to fit.
            const head = [
                `${origin} ${doc.url}`,
                doc.title ? `Title: ${doc.title}` : '',
                doc.description ? `Description: ${doc.description}` : '',
            ].filter(Boolean).join('\n');
            const links = doc.links.slice(0, 25).map(l => `- [${l.kind}] ${l.text || '(untitled)'} — ${l.url}`).join('\n');
            const linkBlock = links ? `\n\nLinks (documents first):\n${links}` : '';
            const room = Math.max(500, textBudget - head.length - linkBlock.length);
            const cut = doc.text.length > room || doc.truncated;
            return `${head}${linkBlock}\n\nPage text${cut ? ' (truncated)' : ''}:\n${doc.text.slice(0, room)}`;
        }
        case 'text': {
            const cut = doc.text.length > textBudget || doc.truncated;
            return `${origin} ${doc.url}${cut ? ' (truncated)' : ''}:\n${doc.text.slice(0, textBudget)}`;
        }
    }
}
